// @ts-nocheck

import { createSelector } from 'reselect';

import {
  getFilterIdFromProps,
  getIsHotFilterSelectedFromProps,
  getLocationHashFromProps,
  getMatchIdFromProps,
} from '../../selectors/_propsGetters_';
import { selectEntitiesBets } from '../../selectors/entities/bets/entitiesBetsPrimary.selector';
import {
  selectEntitiesMatchesEntity,
  selectEntitiesMatchesEntityBets,
} from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOutcomes } from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { selectEntitiesPlayers } from '../../selectors/entities/players/entitiesPlayersPrimary.selector';
import { makeComplexeMatchBetsSelector, selectMatchViewMode, selectMatchHasTMM } from '../../selectors/match.selector';
import { makeMatchIsMyMatchAvailableSelector } from '../../selectors/mymatch.selector';
import BetBuilder from '../../states/betbuilder';
import BetGroup from '../../states/betgroup';
import { BetEntity } from '../../states/entities/bets';
import { MyMatchBetStatuses } from '../../states/mymatch';
import { SpecialBetGroups } from '../../types/bet';
import { MatchViewModes } from '../../types/matchView';
import { getCustomTemplateLabels } from '../../utils/betTemplate';
import { extrudePropertiesFromHash } from '../../utils/location';
import { createDeepEqualSelector, createShallowEqualSelector } from '../../utils/reselect';

const makeMatchGroupsPropsSelector = () => {
  const selectMatchBets = createShallowEqualSelector(
    [selectEntitiesMatchesEntityBets, selectEntitiesBets, selectMatchViewMode],
    (betIds, bets, matchViewMode) =>
      matchViewMode === MatchViewModes.MY_MATCH
        ? betIds.reduce(
            (acc, betId) =>
              bets[betId] && bets[betId].mmStatus !== MyMatchBetStatuses.INACTIVE ? [...acc, bets[betId]] : acc,
            [],
          )
        : betIds.reduce((acc, betId) => (bets[betId] ? [...acc, bets[betId]] : acc), []),
  );

  const selectIsMyMatchAvailable = createSelector(
    [makeMatchIsMyMatchAvailableSelector(), getFilterIdFromProps],
    (matchIsMyMatchAvailable, filterId) => matchIsMyMatchAvailable && !filterId,
  );

  const selectComplexeMatchBets = makeComplexeMatchBetsSelector();

  const selectMatchBetsGroupByBetGroup = createSelector(
    [
      selectEntitiesMatchesEntity,
      selectMatchBets,
      selectMatchHasTMM,
      selectIsMyMatchAvailable,
      selectEntitiesPlayers,
      selectEntitiesOutcomes,
      selectMatchViewMode,
      selectComplexeMatchBets,
      getIsHotFilterSelectedFromProps,
    ],
    (
      match,
      bets,
      matchHasTMM,
      isBetbuilderValid,
      players,
      outcomes,
      matchViewMode,
      complexeMatchBets,
      isHotFilterSelected,
    ) => {
      let hasPlacedTMM = false;
      const hasPlaceBetbuilder = false;

      const matchBetsGroupByBetGroup = complexeMatchBets.reduce(
        (acc, enhancedBet) => {
          const { isMultichoice, bet } = enhancedBet;

          let groupId = null;
          let { betTitle: title, betTypeHelp: helperText } = bet;
          const specialBetGroup = BetGroup.getSpecial(match, bet, bets, isMultichoice);

          if (specialBetGroup) {
            groupId = specialBetGroup;
            const customLabels = getCustomTemplateLabels(specialBetGroup, match, bet);

            if (customLabels) {
              title = customLabels.title;
              // $FlowFixMe
              helperText = customLabels.helperText;
            }
          } else {
            groupId = '' + bet.betGroup;
          }

          if (isMultichoice) {
            groupId += '-multichoice';
          }

          if (isMultichoice && title) {
            title = matchViewMode === MatchViewModes.MY_MATCH ? BetBuilder.addBetTitlePrefix(title) : title;
          }

          if (!acc.groups[groupId]) {
            acc.groups[groupId] = { betsData: [], title, helperText };
            acc.order.push(groupId);
          }

          if (!acc.groups[groupId].betsData.find((betData) => betData.betId === bet.betId)) {
            acc.groups[groupId].betsData.push({ betId: bet.betId, isMultichoice });
          }

          const isBetBuilderReady = !hasPlaceBetbuilder && isBetbuilderValid && !hasPlacedTMM;

          if (
            !hasPlacedTMM &&
            !hasPlaceBetbuilder &&
            isBetBuilderReady &&
            Object.keys(acc.groups).indexOf(groupId) === 0 &&
            BetEntity.isMyMatchMainBet(bet)
          ) {
            if (matchHasTMM && !isHotFilterSelected) {
              acc.groups[SpecialBetGroups.TOP_MY_MATCHS] = SpecialBetGroups.TOP_MY_MATCHS;
              acc.order.push(SpecialBetGroups.TOP_MY_MATCHS);
              hasPlacedTMM = true;
            }
          }

          return acc;
        },
        { groups: {}, order: [] },
      );

      if (matchBetsGroupByBetGroup.order.length && !hasPlaceBetbuilder && !hasPlacedTMM && isBetbuilderValid) {
        if (matchHasTMM && !isHotFilterSelected) {
          matchBetsGroupByBetGroup.groups[SpecialBetGroups.TOP_MY_MATCHS] = SpecialBetGroups.TOP_MY_MATCHS;
          matchBetsGroupByBetGroup.order = [SpecialBetGroups.TOP_MY_MATCHS].concat(matchBetsGroupByBetGroup.order);
        }
      }

      return matchBetsGroupByBetGroup;
    },
  );

  const selectOrderIndexToAutoScroll = createSelector(
    [selectMatchBetsGroupByBetGroup, getLocationHashFromProps, selectEntitiesBets],
    (matchBetsGroupByBetGroup, locationHash, betsEntities) => {
      const { groups, order } = matchBetsGroupByBetGroup;
      const { o: outcomeIdFromHashAsStr, b: betIdFromHashAsStr } = extrudePropertiesFromHash(locationHash);

      if (outcomeIdFromHashAsStr || betIdFromHashAsStr) {
        const groupIds = Object.keys(groups);

        for (const groupId of groupIds) {
          const group = groups[groupId];

          if (typeof group === 'object') {
            const { betsData: groupBetsData } = group;

            if (outcomeIdFromHashAsStr) {
              const outcomeIdFromHash = parseInt(outcomeIdFromHashAsStr, 10);

              for (const { betId: betDataBetId } of groupBetsData) {
                const betEntity = betsEntities[betDataBetId];

                if (betEntity) {
                  const { outcomes: betEntityOutcomeIds } = betEntity;

                  if (~betEntityOutcomeIds.indexOf(outcomeIdFromHash)) {
                    return order.indexOf(groupId);
                  }
                }
              }
            } else {
              const betIdFromHash = parseInt(betIdFromHashAsStr, 10);

              if (groupBetsData?.find(({ betId }) => betId === betIdFromHash)) {
                return order.indexOf(groupId);
              }
            }
          }
        }
      }

      return undefined;
    },
  );

  return createDeepEqualSelector(
    [getMatchIdFromProps, selectMatchBetsGroupByBetGroup, selectMatchViewMode, selectOrderIndexToAutoScroll],
    (matchId, matchBetsGroupByBetGroup, matchViewMode, orderIndexToAutoScroll) => ({
      matchId,
      matchViewMode,
      betGroups: matchBetsGroupByBetGroup.groups,
      order: matchBetsGroupByBetGroup.order,
      orderIndexToAutoScroll,
    }),
  );
};

export default makeMatchGroupsPropsSelector;
